import React, { useEffect } from "react";
import { X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import "./SearchBar.scss";

import { RecentSearchIcon } from "../../../assets/svg";

import { getGlobalSearch, publicRewardsData } from "../../../Services/APIs/LandingPage";

import { defaultTextColor, getFormattedSavingAmount } from "../../../Utils/CommonFunctions";
import { getOfferTypeResult } from "../../../Utils/Constant";
import { ALL_REWARDS, BRAND, BRAND_CATEGORY, OFFER_CATEGORY, OFFER_TYPE, PUBLIC_SEARCH, RECENT_SEARCH } from "../../../Utils/Constants/SearchPage";
import { trackSearch } from "../../../Utils/Analytics";
import { fetchLocalStorageItem, setLocalStorageItem } from "../../../Utils/StorageUtils";

import Spinner from "../../Partials/Loading/Spinner";

import { useAuth } from "../../../hoc/AuthContext";

import useWindowSize from "../../../hooks/useWindowSize";

import ImageWithFallback from "../ImagewithFallback";

// save recent Search
const saveRecentSearch = (searchTerm, setRecentSearchesState) => {
  const recentSearches = JSON.parse(localStorage.getItem('recentSearches')) || [];
  const filteredSearches = recentSearches.filter(term => term !== searchTerm);
  filteredSearches.unshift(searchTerm);
  const updatedSearches = filteredSearches.slice(0, 5);
  localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  setRecentSearchesState(updatedSearches);
};

// group search results by brand
const groupByBrand = (data) => {
  if (!Array.isArray(data)) return [];
  const brandMap = {};
  data.forEach((item) => {
    const brandId = item.brand.id;
    if (!brandMap[brandId]) {
      brandMap[brandId] = {
        brand: {
          id: item.brand.id,
          name: item.brand.name,
          logo: item.brand.logo,
          hex_code: item.brand.hex_code,
          text_hex_code: item.brand.text_hex_code,
          brand_category: item.brand.brand_category,
        },
        offers: [],
      };
    }
    brandMap[brandId].offers.push({
      id: item.id,
      title: item.title,
      saving_amount: getFormattedSavingAmount(item),
      region: item.region,
      offer_category: item.offer_category,
      expiry: item.expiry,
      redemption_channel: item.redemption_channel,
      slug: item.slug,
      offer_type: item?.offer_type,
      brand: item?.brand,
    });
  });
  return Object.values(brandMap);
};

export default function ShowSearchBarResult(props) {
  const { searchLoading = <Spinner />, searchresult = <div>No results found</div>, resultsRef = null, recentSearchesState = [], 
    setRecentSearchesState = () => {}, showSearchBar = false, setShowSearchBar = () => {}, searchTerm = "",
    SetSearchTerm = () => {}, setSearchResult = () => {}, setSearchLoading = () => {}, setShowSearchBarMobile = () => {},
    setShowMenuMobile = () => {}, PublicPage = false, websiteHeaderInfo = {}
  } = props;

  const { isMobile } = useWindowSize();
  const navigate = useNavigate();
  const { isAuthenticated, setShowLogin, setShowSignUp, setCardInfo, subVarientIdGlobal, setWindowLocationUrl } = useAuth();
  const { integratorId, subId } = useParams();
  const queryClient = useQueryClient();

  const searchresultData = searchresult?.length > 0 ? searchresult : "No exact matches found";
  const integratorTheme = queryClient.getQueryData(['integrator-theme', true]) || {};
  const rewardOfferType = ['merchant_funded_offers', 'affiliate_cashback', 'access_n_membership', 'gift_card'];

  // handle change input
  const handleInputChange = (event) => {
    SetSearchTerm(event.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!searchTerm || searchTerm?.length < 3) return;
      setSearchLoading(true);
      try {
        if (PublicPage) {
          const searchparam = `offer_types=${JSON.stringify(rewardOfferType)}&page_name=RewardListingPage&search=${searchTerm}&page=1&limit=10&card_id=${subId || subVarientIdGlobal}`;
          const PublicResult = await publicRewardsData(searchparam);
          if (PublicResult?.status === 200) {
            setSearchResult(PublicResult?.data?.result);
          }
        } else {
          const param = `query=${searchTerm}`;
          const result = await getGlobalSearch(param);
          if (result?.status === 200) {
            setSearchResult(result?.data);
          }
        }
      } catch (e) {
        console.log(e?.data?.error);
      } finally {
        setSearchLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchData();
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        if (isMobile) {
          setShowSearchBarMobile(false);
          setShowMenuMobile(false);
        }
        setShowSearchBar(false);
      }
    }

    if (showSearchBar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchBar]);

  // handle offer type click
  const handleOfferTypeClick = (type, id, name, link) => {
    const searchKeyword = searchTerm || name;
    SetSearchTerm('');
    saveRecentSearch(name, setRecentSearchesState);
    if (type === PUBLIC_SEARCH) {
      trackSearch({
        searchTerm: searchKeyword,
        searchSource: "global_search",
        resultType: type,
        resultId: id,
        resultName: name,
        destinationUrl: link,
        pageType: "global_search",
      });
      setShowMenuMobile(false);
      setShowSearchBarMobile(false);
      setShowSearchBar(false);
      SetSearchTerm('');
      setSearchResult(null);
      navigate(link);
      window.location.reload();
      return;
    }

    if (type === RECENT_SEARCH) {
      SetSearchTerm(name);
      return;
    }

    let path = PublicPage ? `/rewards/${integratorId}/${subId || subVarientIdGlobal}` : '/rewards';
    const queryParams = new URLSearchParams();

    if (isAuthenticated) {
      queryParams.set('card_id', subVarientIdGlobal);
    }
    queryParams.delete('filters');
    queryParams.delete('offer_types');

    switch (type) {
      case OFFER_CATEGORY:
        queryParams.set('offer_types', JSON.stringify(rewardOfferType));
        queryParams.set('filters', JSON.stringify({ offer_categories: [id] }));
        break;
      case BRAND_CATEGORY:
        queryParams.set('offer_types', JSON.stringify(rewardOfferType));
        queryParams.set('filters', JSON.stringify({ brand_categories: [id] }));
        break;
      case OFFER_TYPE:
        queryParams.set('offer_types', JSON.stringify([id]));
        break;
      case BRAND:
        queryParams.set('offer_types', JSON.stringify(rewardOfferType));
        queryParams.set('filters', JSON.stringify({ brands: [id] }));
        break;
      default:
        return;
    }

    const intendedUrl = `${path}?${queryParams.toString()}`;
    trackSearch({
      searchTerm: searchKeyword,
      searchSource: "global_search",
      resultType: type,
      resultId: id,
      resultName: name,
      destinationUrl: intendedUrl,
      pageType: "global_search",
    });

    if (!isAuthenticated) {
      setWindowLocationUrl(intendedUrl);
      setShowLogin(true);
      setShowSignUp(false);
      let newData = {
        search: true,
        url: intendedUrl,
      };
      setCardInfo(newData);
    } else {
      navigate(intendedUrl, {
        state: {
          fromSearch: true,
          isCardUrl: true,
          cardURL: type === OFFER_TYPE ? id : ALL_REWARDS,
        },
      });
    }

    setShowMenuMobile(false);
    setShowSearchBarMobile(false);
    setShowSearchBar(false);
    SetSearchTerm('');
    setSearchResult(null);
  };

  const recentSearches = JSON.parse(fetchLocalStorageItem('recentSearches')) || [];

  // remove search from localstorage
  const removeSearchFromLocalStorage = (searchToRemove) => {
    const updatedSearches = recentSearches.filter(search => search !== searchToRemove);
    setLocalStorageItem('recentSearches', JSON.stringify(updatedSearches));
    setRecentSearchesState(updatedSearches);
  };

  // get offer label
  const getOfferLabel = (search) => {
    switch (search) {
      case 'merchant_funded_offers':
        return 'Discount Offers';
      case 'affiliate_cashback':
        return 'Cashback';
      case 'gift_card':
        return 'Gift Cards';
      case 'access_n_membership':
        return 'Access & Membership';
      default:
        return search;
    }
  };

  // const [r, g, b] = hexToRgb(backgroundColor(integratorTheme));
  // const backgroundColorRecent = `rgba(${r}, ${g}, ${b}, 0.8)`;

  // handle Close Search button
  const handleCloseSearchButton = () => {
    setShowMenuMobile(false);
    setShowSearchBarMobile(false);
    SetSearchTerm('');
    setShowSearchBar(false);
  };

  function renderPublicSearchResult() {
    const searchresultData = Array.isArray(searchresult) ? searchresult : [];
    const groupedData = groupByBrand(searchresultData);

    return (
      <>
        {groupedData?.length > 0 ? (
          <div className="searchResultsContainer">
            {groupedData?.map((brandData) => (
              <div key={brandData?.brand?.id} className="brandContainer">
                <div className="brandHeader">
                  <ImageWithFallback
                    src={brandData?.brand?.logo}
                    alt={brandData?.brand?.name}
                    className="brandLogo"
                  />
                  <div className="brandName">{brandData?.brand?.name}</div>
                </div>
                <div className="offersContainer">
                  {brandData.offers.map((offer, offerIndex) => {
                    const BrandName = offer?.brand?.name?.replace(/ /g, "-");
                    const link = `/rewards/${BrandName}/${integratorId}/${subId}/${offer?.id}`;
                    return (
                      <div key={offer.id} className="offerItem"
                        onClick={() => handleOfferTypeClick(PUBLIC_SEARCH, null, offer?.brand?.name, link)}
                      >
                        <div className="offerDetails">
                          <div title={offer?.saving_amount} className="offerSaving">{offer?.saving_amount}</div>
                        </div>
                        <div className="offerMeta">
                          <div className="offerRegion">{offer?.region?.name}</div>
                          <div className="offerType">{getOfferTypeResult(offer?.offer_type)?.title}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (searchTerm?.length > 0 ?
          <div className="noResults">No results found for "{searchTerm}"</div> : ""
        )}
      </>
    );
  };

  // render Offer type
  function renderOfferType (result, OfferType) {
    return(
      <div className="searchItemContent offerType" onClick={() => handleOfferTypeClick(OFFER_TYPE, result?.name, result?.name)}>
        {OfferType && <OfferType />}
        <div className="searchItemName">{getOfferTypeResult(result?.name)?.title}</div>
      </div>
    );
  };

  // render Offer Category
  function renderOfferCategory (result) {
    return(
       <div className="searchItemContent offerCategory" 
        onClick={() => handleOfferTypeClick(OFFER_CATEGORY, result?.id, result?.name)}>
        <div className="searchItemName">Spend Type - {result?.name}</div>
      </div>
    );
  };

  // render Brand Category
  function renderBrandCategory (result) {
    return(
      <div className="searchItemContent brandCategory" onClick={() => handleOfferTypeClick(BRAND_CATEGORY, result?.id, result?.name)}>
        {result?.bg_image ? (
          <ImageWithFallback className="searchItemImage bgImage" src={result?.bg_image} alt={result?.name} />
        ) : (
          <div className="searchItemPlaceholder" />
        )}
        <div className="searchItemName">{result?.name}</div>
      </div>
    );
  };

  // render Brand
  function renderBrand (result) {
    return(
      <div className="searchItemContent brand" onClick={() => handleOfferTypeClick(BRAND, result?.id, result?.name)}>
        <ImageWithFallback
          className="searchItemImage"
          style={{ "--item-bg-color": `#${result?.hex_code}` }}
          src={result?.logo}
          alt={result?.name}
        />
        <div className="searchItemDetails">
          <div className="searchItemName">{result?.name}</div>
          {result?.tag_line && (
            <div className="searchItemTagline">{result?.tag_line}</div>
          )}
        </div>
      </div>
    );
  };

  function renderPrivateSearchResult() {
    const searchresultData = Array.isArray(searchresult) ? searchresult : [];
    return (
      <>
        {Array.isArray(searchresultData) && searchresultData.length > 0 ? (
          <div className="searchResultsContainer">
            {searchresultData.map((result, index) => {
              const OfferType = getOfferTypeResult(result?.name)?.Icon;
              return (
                <div key={index} className="searchItem">
                  {result?.source === BRAND ? (
                    renderBrand(result)
                  ) : result?.source === OFFER_TYPE ? (
                    renderOfferType(result, OfferType)
                  ) : result?.source === OFFER_CATEGORY ? (
                    renderOfferCategory(result)
                  ) : result?.source === BRAND_CATEGORY ? (
                    renderBrandCategory(result)
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="noResults">{searchresultData}</div>
        )}
      </>
    );
  }

  return (showSearchBar &&
    <>
      <div className="searchOverlay"></div>
      <div
        className="searchContainer"
        onClick={(e) => e.stopPropagation()}
        style={{ "--theme-default-text-color": defaultTextColor(integratorTheme) }}
      >
        <div className="searchPopup" ref={resultsRef}>
          {/* search header */}
          <div className="searchHeader">
            <div className="searchTitle">Search</div>
            <X color="#3B3E63" size={26} onClick={() => handleCloseSearchButton()} className="searchClose" />
          </div>

          {/* search input container */}
          <div className="searchInputContainer">
            <input
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              placeholder={websiteHeaderInfo?.is_search_placeholder_present ? websiteHeaderInfo?.search_text || "search" : ""}
              className="searchInput"
            />
          </div>

          {/* search result container */}
          <div className="searchResults">
            {searchLoading ? <div className="searchLoading"><Spinner /></div> : (
              PublicPage ? renderPublicSearchResult() : renderPrivateSearchResult()
            )}

            {/* recent searches */}
            {recentSearchesState.length > 0 && (
              <div className="recentSearches">
                <h3 className="recentSearchesTitle">Recent Searches</h3>
                <ul className="recentSearchesList">
                  {recentSearchesState.map((search, index) => (
                    <li key={index} className="recentSearchItem">
                      <div className="recentSearchContent" onClick={() => handleOfferTypeClick(RECENT_SEARCH, null, getOfferLabel(search))}>
                        <RecentSearchIcon className="recentSearchIcon" />
                        <span className="recentSearchText">{getOfferLabel(search)}</span>
                      </div>
                      <X size={14} className="recentSearchRemove" onClick={() => removeSearchFromLocalStorage(search)} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

ShowSearchBarResult.propTypes = {
  searchLoading: PropTypes.bool,
  searchresult: PropTypes.array,
  resultsRef: PropTypes.object,
  recentSearchesState: PropTypes.array,
  setRecentSearchesState: PropTypes.func,
  showSearchBar: PropTypes.bool,
  setShowSearchBar: PropTypes.func,
  searchTerm: PropTypes.string,
  SetSearchTerm: PropTypes.func,
  setSearchResult: PropTypes.func,
  setSearchLoading: PropTypes.func,
  setShowSearchBarMobile: PropTypes.func,
  setShowMenuMobile: PropTypes.func,
  PublicPage: PropTypes.bool,
  websiteHeaderInfo: PropTypes.object,
};
